// Product recognition review queue, slices, thresholds and verdicts.
import { ApiCleaningToolUPCData } from '../../types/cleaningTool';
import {
  RecognitionBulkConfirmRequest,
  RecognitionBulkConfirmResponse,
  RecognitionConfidentCorrectBucketCleanupResponse,
  RecognitionOkrAggregatesParams,
  RecognitionOkrAggregatesResponse,
  RecognitionReviewAccessResponse,
  RecognitionReviewBarcodeLookupResponse,
  RecognitionReviewQueueParams,
  RecognitionReviewQueueResponse,
  RecognitionReviewRequest,
  RecognitionReviewSaveResponse,
  RecognitionReviewSliceParams,
  RecognitionReviewSliceResponse,
  RecognitionReviewUnitDetailResponse,
  RecognitionReviewUnitParams,
  RecognitionSamplingStrategyRequest,
  RecognitionSamplingStrategyResponse,
  RecognitionThresholdConfiguration,
  RecognitionThresholdDraftRequest,
} from '../../types/recognitionReview';
import { forgeRequest, forgeVoidRequest } from '../forgeRequest';
import { FORGE_PR_REVIEW_API_URL } from './endpoints';

export async function fetchRecognitionReviewItem(
  token: string,
  itemNumber: string,
  chainId: number
): Promise<ApiCleaningToolUPCData> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<ApiCleaningToolUPCData>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/catalog/item-number/${encodeURIComponent(itemNumber)}?${params}`,
    'Error fetching Recognition catalog evidence'
  );
}

export async function fetchRecognitionReviewItemByBarcode(
  token: string,
  barcodeQuery: string,
  chainId: number
): Promise<RecognitionReviewBarcodeLookupResponse> {
  const params = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RecognitionReviewBarcodeLookupResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/catalog/barcode/${encodeURIComponent(barcodeQuery)}?${params}`,
    'Error fetching Recognition catalog evidence by barcode'
  );
}

/**
 * Uses API-DL's exact admin-group decision to gate the two Recognition admin surfaces without
 * adding a new permission or changing API Main.
 */
export async function fetchRecognitionReviewAccess(
  token: string
): Promise<RecognitionReviewAccessResponse> {
  return forgeRequest<RecognitionReviewAccessResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/access`,
    'Error checking Recognition review access'
  );
}

export async function fetchRecognitionThresholdConfiguration(
  token: string
): Promise<RecognitionThresholdConfiguration> {
  return forgeRequest<RecognitionThresholdConfiguration>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confidence-thresholds`,
    'Error loading Recognition thresholds'
  );
}

export async function saveRecognitionThresholdDraft(
  token: string,
  request: RecognitionThresholdDraftRequest
): Promise<RecognitionThresholdConfiguration> {
  return forgeRequest<RecognitionThresholdConfiguration>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confidence-thresholds/draft`,
    'Error saving Recognition threshold draft',
    { method: 'PUT', body: request }
  );
}

export async function activateRecognitionThresholdRevision(
  token: string,
  revisionId: string
): Promise<void> {
  return forgeVoidRequest(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confidence-thresholds/${encodeURIComponent(revisionId)}/activate`,
    'Error activating Recognition thresholds',
    { method: 'POST' }
  );
}

export async function clearConfidentCorrectErrorBuckets(
  token: string
): Promise<RecognitionConfidentCorrectBucketCleanupResponse> {
  return forgeRequest<RecognitionConfidentCorrectBucketCleanupResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/confident-correct/error-buckets`,
    'Error removing confident-correct error buckets',
    { method: 'DELETE' }
  );
}

function appendRecognitionPopulationQuery(
  query: URLSearchParams,
  params: Pick<RecognitionOkrAggregatesParams, 'modPrefix' | 'collectorValues' | 'reviewSetId'>
): void {
  if (params.modPrefix) query.set('mod_prefix', params.modPrefix);
  for (const email of params.collectorValues ?? []) {
    query.append('collector_values', email);
  }
  if (params.reviewSetId) query.set('review_set_id', params.reviewSetId);
}

/**
 * Lists capture units awaiting Recognition review for one chain week, in the requested stable
 * server-side order. Omitting `weekStart` lets the backend resolve the default week and echo it
 * on the response.
 *
 * `modTag`, `modPrefix`, `collectorValues` (repeatable exact emails), `capturedDate`, `strategy`,
 * and `cell` narrow the queue server-side; the response echoes the filters it actually applied.
 * The OKRs dashboard reads its unit table here with `strategy=representative`.
 */
export async function fetchRecognitionReviewQueue(
  token: string,
  params: RecognitionReviewQueueParams
): Promise<RecognitionReviewQueueResponse> {
  const query = new URLSearchParams();
  if (params.chainId !== null && params.chainId !== undefined) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.period) query.set('period', params.period);
  if (params.capturedFrom) query.set('captured_from', params.capturedFrom);
  if (params.capturedTo) query.set('captured_to', params.capturedTo);
  if (params.weekStart) query.set('week_start', params.weekStart);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.modTag) query.set('mod_tag', params.modTag);
  appendRecognitionPopulationQuery(query, params);
  if (params.capturedDate) query.set('captured_date', params.capturedDate);
  if (params.capturedDateTo) query.set('captured_date_to', params.capturedDateTo);
  if (params.strategy) query.set('strategy', params.strategy);
  params.cell?.forEach((cell) => query.append('cell', cell));
  if (params.bucket) query.set('bucket', params.bucket);
  if (params.reviewedOnly) query.set('reviewed_only', 'true');
  if (params.sort) query.set('sort', params.sort);

  const requestKey = `${token}:${query.toString()}`;
  return forgeRequest<RecognitionReviewQueueResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/queue?${query.toString()}`,
    'Error fetching Recognition review queue',
    { dedupeKey: ['fetchRecognitionReviewQueue', requestKey] }
  );
}

/**
 * Fetches the weekly Recognition OKR aggregates: both outcome grids, adjustment reasons, scan
 * issues, and the bucket Pareto. Metrics only - unit rows come from the paginated queue endpoint.
 */
export async function fetchRecognitionOkrAggregates(
  token: string,
  params: RecognitionOkrAggregatesParams
): Promise<RecognitionOkrAggregatesResponse> {
  const query = new URLSearchParams();
  if (params.chainId !== null && params.chainId !== undefined) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.period) query.set('period', params.period);
  if (params.capturedFrom) query.set('captured_from', params.capturedFrom);
  if (params.capturedTo) query.set('captured_to', params.capturedTo);
  if (params.weekStart) query.set('week_start', params.weekStart);
  appendRecognitionPopulationQuery(query, params);
  params.reviewClass?.forEach((value) => query.append('review_class', value));
  params.eligibility?.forEach((value) => query.append('eligibility', value));
  params.identity?.forEach((value) => query.append('identity', value));
  params.scanState?.forEach((value) => query.append('scan_state', value));
  params.cell?.forEach((value) => query.append('cell', value));
  if (params.bucket) query.set('bucket', params.bucket);
  if (params.topKRanking) query.set('top_k_ranking', params.topKRanking);
  if (params.topK) query.set('top_k', String(params.topK));
  if (params.countBy) query.set('count_by', params.countBy);

  const requestKey = `${token}:${query.toString()}`;
  return forgeRequest<RecognitionOkrAggregatesResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/aggregates?${query.toString()}`,
    'Error fetching Recognition OKR aggregates',
    { dedupeKey: ['fetchRecognitionOkrAggregates', requestKey] }
  );
}

/** Lists individual reviewed samples matching the complete dashboard filter intersection. */
export async function fetchRecognitionReviewSlice(
  token: string,
  params: RecognitionReviewSliceParams
): Promise<RecognitionReviewSliceResponse> {
  const query = new URLSearchParams();
  if (params.chainId !== null && params.chainId !== undefined) {
    query.set('chain_id', String(params.chainId));
  }
  if (params.period) query.set('period', params.period);
  if (params.capturedFrom) query.set('captured_from', params.capturedFrom);
  if (params.capturedTo) query.set('captured_to', params.capturedTo);
  appendRecognitionPopulationQuery(query, params);
  params.reviewClass?.forEach((value) => query.append('review_class', value));
  params.eligibility?.forEach((value) => query.append('eligibility', value));
  params.identity?.forEach((value) => query.append('identity', value));
  params.scanState?.forEach((value) => query.append('scan_state', value));
  params.cell?.forEach((value) => query.append('cell', value));
  if (params.bucket) query.set('bucket', params.bucket);
  if (params.topKRanking) query.set('top_k_ranking', params.topKRanking);
  if (params.topK) query.set('top_k', String(params.topK));
  if (params.limit) query.set('limit', String(params.limit));
  return forgeRequest<RecognitionReviewSliceResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/slice?${query.toString()}`,
    'Error fetching Recognition review slice',
    { signal: params.signal }
  );
}

/**
 * Fetches one capture unit's samples with their frozen recognition evidence. An omitted `modTag`
 * matches the samples captured without a mod tag, which is a distinct unit from any tagged one.
 */
export async function fetchRecognitionReviewUnit(
  token: string,
  params: RecognitionReviewUnitParams
): Promise<RecognitionReviewUnitDetailResponse> {
  const query = new URLSearchParams({ chain_id: String(params.chainId) });
  if (params.modTag) query.set('mod_tag', params.modTag);

  const requestKey = `${token}:${params.imageId}:${query.toString()}`;
  return forgeRequest<RecognitionReviewUnitDetailResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/${encodeURIComponent(String(params.imageId))}?${query.toString()}`,
    'Error fetching Recognition review unit',
    { dedupeKey: ['fetchRecognitionReviewUnit', requestKey] }
  );
}

/**
 * Saves one reviewer verdict and returns the outcome the backend scored and stored for it, plus
 * the sample's post-write `updatedAt` for the next verdict's optimistic-concurrency token.
 */
export async function saveRecognitionReview(
  token: string,
  evaluationSampleId: string,
  request: RecognitionReviewRequest
): Promise<RecognitionReviewSaveResponse> {
  return forgeRequest<RecognitionReviewSaveResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/samples/${encodeURIComponent(evaluationSampleId)}/review`,
    'Error saving Recognition review',
    { method: 'PUT', body: request }
  );
}

/** Confirm every currently eligible confident-correct scan with one unit-scoped request. */
export async function bulkConfirmConfidentCorrect(
  token: string,
  chainId: number,
  request: RecognitionBulkConfirmRequest
): Promise<RecognitionBulkConfirmResponse> {
  return forgeRequest<RecognitionBulkConfirmResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/bulk-confirm-confident-correct?chain_id=${encodeURIComponent(String(chainId))}`,
    'Error confirming confident correct scans',
    { method: 'PUT', body: request }
  );
}

/** Confirm visible adjusted scans, at either confidence level, in one capture unit. */
export async function bulkConfirmAdjusted(
  token: string,
  chainId: number,
  request: RecognitionBulkConfirmRequest
): Promise<RecognitionBulkConfirmResponse> {
  return forgeRequest<RecognitionBulkConfirmResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/bulk-confirm-adjusted?chain_id=${encodeURIComponent(String(chainId))}`,
    'Error confirming adjusted scans',
    { method: 'PUT', body: request }
  );
}

/**
 * Stamps every sample of one capture unit with the strategy its crops were collected under.
 * The write does not change any review concurrency token.
 */
export async function setRecognitionSamplingStrategy(
  token: string,
  chainId: number,
  request: RecognitionSamplingStrategyRequest
): Promise<RecognitionSamplingStrategyResponse> {
  const query = new URLSearchParams({ chain_id: String(chainId) });
  return forgeRequest<RecognitionSamplingStrategyResponse>(
    token,
    `${FORGE_PR_REVIEW_API_URL}/units/sampling-strategy?${query.toString()}`,
    'Error updating Recognition sampling strategy',
    { method: 'PUT', body: request }
  );
}
